/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { isNone, isEmpty } from '@ember/utils';
import { task } from 'ember-concurrency';

import EnvironmentModel from 'cio/models/environment';
import WhitelistedIpModel from 'cio/models/whitelisted-ip';
import FormValidations from 'cio/utils/form-validations';
import isValidIpAddress from 'cio/utils/validators/ip-address';
import { tracked } from '@glimmer/tracking';

const Validations = {
  ip(value: string) {
    if (isNone(value) || isEmpty(value.trim())) {
      return 'Please enter an IP address';
    }

    if (!isValidIpAddress(value)) {
      return 'Please enter a valid IP address';
    }

    return true;
  },

  environment(value: EnvironmentModel | null) {
    if (!value) {
      return 'Please select a workspace';
    }

    return true;
  },
};

interface ApiCredentialsWhitelistTableRowComponentArgs {
  address: WhitelistedIpModel;
  addresses: WhitelistedIpModel[];
  readOnly?: boolean;
  environments: EnvironmentModel[];
  onConfirmDelete(address: WhitelistedIpModel): unknown;
  onCreateSubmit(address: WhitelistedIpModel): unknown;
}

export default class ApiCredentialsWhitelistTableRowComponent extends Component<ApiCredentialsWhitelistTableRowComponentArgs> {
  isCreating = false;
  @tracked validations: FormValidations;

  constructor(owner: unknown, args: ApiCredentialsWhitelistTableRowComponentArgs) {
    super(owner, args);

    this.validations = FormValidations.create({ validations: Validations, context: this, modelKey: 'args.address' });
  }

  get readOnly() {
    return this.args.readOnly ?? false;
  }

  get environmentSelectOptions() {
    return [
      { label: 'Select workspace…', value: '' },
      ...this.args.environments.map((environment: EnvironmentModel) => ({
        label: environment.name,
        value: environment.id,
      })),
    ];
  }

  @computed('validations.{isTouched,hasErrors}', 'args.address.hasDirtyAttributes', 'submitTask.isRunning', 'readOnly')
  get submitDisabled() {
    return (
      !this.validations.isTouched ||
      this.validations.hasErrors ||
      !this.args.address.hasDirtyAttributes ||
      this.submitTask.isRunning ||
      this.readOnly
    );
  }

  get isOnlyWhitelistedIpForEnv() {
    let { address, addresses } = this.args;
    return addresses.filter((addr) => addr.environment.id === address.environment.id).length === 1;
  }

  submitTask = task(async () => {
    try {
      await this.validations.validateModel();
    } catch (e) {
      // `validateModel` already set validation errors, return early without submitting
      return;
    }
    await this.args.onCreateSubmit(this.args.address);
    this.isCreating = false;
  });

  willDestroy() {
    super.willDestroy();

    if (this.isCreating) {
      this.args.address.rollbackAttributes();
    }
  }

  @action
  cancelCreating() {
    this.isCreating = false;
    this.args.address.rollbackAttributes();
  }

  @action
  remove() {
    this.args.onConfirmDelete(this.args.address);
  }

  @action
  setEnvironment(value: string) {
    this.args.address.set('environment', value ? this.args.environments.findBy('id', value) : null);
    this.validations.validateAttribute('environment');
  }

  @action
  onIpChange(ip: string) {
    this.args.address.set('ip', ip);
    this.validations.validateAttribute('ip');
  }
}
